import { useState, useCallback, useContext } from 'react';
import noop from 'lodash/noop';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { OPERATION_IDS } from '@atlassian/jira-issue-operations-commons/src/constants.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { asyncWrmRequire } from '@atlassian/jira-wrm/src/async-wrm.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { issueOperationsCacheContext } from '../utils/state-cache.tsx';
import type {
	Action,
	Operation,
	GetActionsAndOperationsResponse,
	IssueLink,
	ActionsAndOperationsResponse,
} from './types.tsx';

export type DataState = {
	data?: {
		actions?: Action[];
		operations?: Operation[];
		links?: IssueLink[];
	};
	loading: boolean;
	error?: boolean;
	loadFieldResources: () => void;
};

const useOperationsAndActions = () => {
	let state: DataState;
	let setState: React.Dispatch<React.SetStateAction<DataState>>;
	if (fg('empanada_nin_convert_issue_ops_to_entrypoint')) {
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		const { operationsAndActions } = useContext(issueOperationsCacheContext);
		[state, setState] = operationsAndActions;
	} else {
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		[state, setState] = useState<DataState>({
			loading: false,
			loadFieldResources: noop,
		});
	}

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const toOperation = (
		issueId: number,
		rawOperation: GetActionsAndOperationsResponse,
	): Operation | undefined => {
		const styleAndUrlMap = new Map<string, { styleClass: string; url: string }>([
			[
				'com.pyxis.greenhopper.jira:greenhopper-rank-bottom-operation',
				{
					styleClass: OPERATION_IDS.RANK_TO_BOTTOM,
					url: `/secure/RankBottom.jspa?issueId=${issueId}`,
				},
			],
			[
				'com.pyxis.greenhopper.jira:greenhopper-rank-top-operation',
				{
					styleClass: OPERATION_IDS.RANK_TO_TOP,
					url: `/secure/RankTop.jspa?issueId=${issueId}`,
				},
			],
			[
				'com.pyxis.greenhopper.jira:greenhopper-rapidboard-operation',
				{
					styleClass: OPERATION_IDS.AGILE_BOARD,
					url: `/secure/GHGoToBoard.jspa?issueId=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugins.jira-development-integration-plugin:devstatus.cta.createbranch',
				{
					styleClass: OPERATION_IDS.CREATE_BRANCH,
					url: '#devstatus.cta.createbranch',
				},
			],
			[
				'com.atlassian.jira.plugins.jira-dnd-attachment-plugin:show-attach-screenshot-popup-button',
				{
					styleClass: OPERATION_IDS.ATTACH_SCREENSHOT,
					url: `/secure/ShowAttachScreenshotFormAction!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugins.jira-dnd-attachment-plugin:unified-attach-file',
				{
					styleClass: OPERATION_IDS.ATTACH_FILE,
					url: `/secure/AttachFile!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugins.jira-admin-helper-plugin:viewissue-notification-trigger-opsbar',
				{
					styleClass: OPERATION_IDS.NOTIFICATION_HELPER,
					url: '/notimportant',
				},
			],
			[
				'com.atlassian.jira.plugins.jira-admin-helper-plugin:viewissue-permissionhelper-trigger-opsbar',
				{
					styleClass: OPERATION_IDS.PERMISSION_HELPER,
					url: '/notimportant',
				},
			],
			[
				'com.atlassian.jira.plugins.jira-admin-helper-plugin:viewissue-fieldhelper-trigger-opsbar',
				{
					styleClass: OPERATION_IDS.FIELD_HELPER,
					url: '/notimportant',
				},
			],
			[
				'com.atlassian.jira.jira-project-config-plugin:add-custom-field',
				{
					styleClass: OPERATION_IDS.ADD_FIELD,
					url:
						// We definitely do not want an exposure here, its to generate link information the user hasn't seen the option yet
						UNSAFE_noExposureExp('jira-admin-pages-centralised-fields-m1-experiment')?.[0].get(
							'isEnabled',
							false,
						)
							? '/jira/settings/issues/fields'
							: '/secure/admin/ViewCustomFields.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:delete-issue',
				{
					styleClass: OPERATION_IDS.DELETE,
					url: `/secure/DeleteIssue!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:edit-labels',
				{
					styleClass: OPERATION_IDS.LABELS,
					url: `/secure/EditLabels!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:clone-issue',
				{
					styleClass: OPERATION_IDS.CLONE,
					url: `/secure/CloneIssueDetails!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:link-issue',
				{
					styleClass: OPERATION_IDS.LINK,
					url: `/secure/LinkJiraIssue!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:move-subtask',
				{
					styleClass: OPERATION_IDS.MOVE_SUBTASK,
					url: `/secure/MoveSubTaskChooseOperation!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:move-issue',
				{
					styleClass: OPERATION_IDS.MOVE,
					url: `/secure/MoveIssue!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:issue-to-subtask',
				{
					styleClass: OPERATION_IDS.CONVERT_TO_SUBTASK,
					url: `/secure/ConvertIssue.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:subtask-to-issue',
				{
					styleClass: OPERATION_IDS.CONVERT_TO_ISSUE,
					url: `/secure/ConvertSubTask.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:create-subtask',
				{
					styleClass: OPERATION_IDS.CREATE_SUBTASK,
					url: `/secure/CreateSubTaskIssue!default.jspa?parentIssueId=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:manage-watchers',
				{
					styleClass: OPERATION_IDS.WATCHERS,
					url: `/secure/ManageWatchers!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:unwatch-issue',
				{
					styleClass: OPERATION_IDS.UNWATCH_ISSUE,
					url: '/secure/VoteOrWatchIssue.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:watch-issue',
				{
					styleClass: OPERATION_IDS.WATCH_ISSUE,
					url: '/secure/VoteOrWatchIssue.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:view-voters',
				{
					styleClass: OPERATION_IDS.VIEW_VOTERS,
					url: `/secure/ViewVoters!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:unvote-issue',
				{
					styleClass: OPERATION_IDS.REMOVE_VOTE,
					url: '/secure/VoteOrWatchIssue.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:vote-issue',
				{
					styleClass: OPERATION_IDS.ADD_VOTE,
					url: '/secure/VoteOrWatchIssue.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:log-work',
				{
					styleClass: OPERATION_IDS.LOG_WORK,
					url: `/secure/CreateWorklog!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:comment-issue',
				{
					styleClass: OPERATION_IDS.COMMENT_ISSUE,
					url: `/secure/AddComment!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:assign-to-me',
				{
					styleClass: OPERATION_IDS.ASSIGN_TO_ME,
					url: '/secure/AssignIssue.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:assign-issue',
				{
					styleClass: OPERATION_IDS.ASSIGN,
					url: '/secure/AssignIssue!default.jspa',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:edit-issue',
				{
					styleClass: OPERATION_IDS.EDIT_ISSUE,
					url: `/secure/EditIssue!default.jspa?id=${issueId}`,
				},
			],
			[
				'com.atlassian.servicedesk.approvals-plugin:approve-approval',
				{
					styleClass: OPERATION_IDS.APPROVE_APPROVAL,
					url: '#',
				},
			],
			[
				'com.atlassian.servicedesk.approvals-plugin:reject-approval',
				{
					styleClass: OPERATION_IDS.REJECT_APPROVAL,
					url: '#',
				},
			],
			[
				'com.atlassian.jira.jira-quick-edit-plugin:create-linked-issue-item',
				{
					styleClass: OPERATION_IDS.CREATE_LINKED_ISSUE,
					url: '#',
				},
			],
			[
				'com.atlassian.jira.plugin.system.issueoperations:archive-issue',
				{
					styleClass: OPERATION_IDS.ARCHIVE,
					url: '#',
				},
			],
		]);

		const styleAndUrlProps = styleAndUrlMap.get(rawOperation.operation);

		if (!styleAndUrlProps) {
			// we could not create a valid operation based on the fetch data
			return undefined;
		}

		return {
			...rawOperation,
			...styleAndUrlProps,
		};
	};

	const fetchData = useCallback(
		async (issueId: number): Promise<void> => {
			setState((currentState) => ({
				...currentState,
				loading: true,
			}));
			try {
				const response: ActionsAndOperationsResponse = await Promise.all([
					fetchJson(`/rest/api/1.0/issues/${issueId}/GetActionsAndOperations`),
					fetchJson(`/rest/viewIssue/1/remoteIssueLink/linkType?issueId=${issueId}`),
					asyncWrmRequire(['wrc!com.atlassian.jira.jira-project-config-plugin:custom-fields']).then(
						() =>
							asyncWrmRequire([
								'wrc!com.atlassian.jira.jira-project-config-plugin:custom-fields-impl',
							]),
					),
				]);

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				if (window?.JIRA?.Marionette?.Application) {
					// Fill in the missing globals, taken from jira-components/jira-legacy-frontend/jira-issue-search-frontend/jira-global-issue-navigator-frontend/src/main/resources/content/js/Application.js

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					const issuesApplication = new window.JIRA.Marionette.Application();

					// Handlers required for Add Field
					issuesApplication.reqres.setHandler('issueEditor:fields', () => []);
					issuesApplication.reqres.setHandler('issueEditor:refreshIssue', () =>
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						window.AJS.$.Deferred().resolve(),
					);

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					window.JIRA.Issues.Application = issuesApplication;
				}

				const { operations: operationsRaw = [] } = response[0];

				let finalOperations: Operation[] | undefined;
				let mappedOperations: (Operation | undefined)[] = [];

				if (operationsRaw.length) {
					mappedOperations = operationsRaw
						.map((rawOperation) => {
							// If url and styleCass exist, this is a connect addon operation
							// and we don't need to look up these from the operations map
							if (rawOperation.url && rawOperation.styleClass) {
								return {
									name: rawOperation.name,
									styleClass: rawOperation.styleClass,
									url: rawOperation.url,
									operation: rawOperation.operation,
									desc: rawOperation.desc,
								};
							}

							// There is no url/styleClass so we need to look these up
							const mappedOperation = toOperation(issueId, rawOperation);

							if (!mappedOperation) {
								fireOperationalAnalytics(
									createAnalyticsEvent({
										type: 'sendOperationalEvent',
										action: 'failed',
										actionSubject: 'getIssueOperationUrl',
									}),
									'getIssueOperationUrl failed',
									{
										operation: rawOperation.operation,
									},
								);
							}
							return mappedOperation;
						})
						// filter out un-mappable operations
						.filter((operation) => !!operation);
				}

				// for typecheck: proving mappedOperations after filtering is of the correct type Operation[]
				if (isOperationList(mappedOperations)) {
					finalOperations = mappedOperations;
				}

				setState({
					data: {
						actions: response[0].actions ?? [],
						operations: finalOperations ?? [],
						links: response[1] ?? [],
					},
					loading: false,
					error: false,
					loadFieldResources: (elementToFocusOnClose?: string) =>
						// eslint-disable-next-line @typescript-eslint/no-explicit-any, jira/jira-ssr/no-unchecked-globals-usage
						window.require(['jira-project-config/custom-fields/api'], (api: any) =>
							api.addCustomFieldToIssue(issueId, { wizardOptions: { elementToFocusOnClose } }),
						),
				});

				fireOperationalAnalytics(
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						action: 'success',
						actionSubject: 'fetchOperationsAndFieldsDialog',
					}),
					'fetchOperationsAndFieldsDialog success',
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					error: e,
					meta: {
						id: 'fetchOperationsAndCustomFields',
						packageName: 'jiraIssueNavigatorIssueOperationsServices',
						teamName: 'empanada',
					},
				});
				setState({
					data: undefined,
					loading: false,
					error: true,
					loadFieldResources: noop,
				});
			}
		},
		[createAnalyticsEvent, setState],
	);

	const cleanUpState = (): void =>
		setState({
			loading: false,
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			loadFieldResources: () => {},
		});

	return { fetchData, state, cleanUpState };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isOperationList(list: any[] = []): list is Operation[] {
	return list.every(
		(item) =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			typeof (item as Operation).operation === 'string' &&
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			typeof (item as Operation).name === 'string' &&
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			typeof (item as Operation).url === 'string' &&
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			typeof (item as Operation).styleClass === 'string',
	);
}

export default useOperationsAndActions;
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { Operation, IssueLink };
